import { create } from 'zustand';
import type { Tables } from '../lib/supabase/types';

type Notification = Tables<'user_notifications'>;

interface NotificationState {
  notifications: Notification[];
  unreadCount: number;
  isLoading: boolean;
  soundEnabled: boolean;
  
  // Actions
  setNotifications: (notifications: Notification[]) => void;
  addNotification: (notification: Notification) => void;
  markAsRead: (notificationId: number) => void;
  markAllAsRead: () => void;
  removeNotification: (notificationId: number) => void;
  setUnreadCount: (count: number) => void;
  setSoundEnabled: (enabled: boolean) => void;
  setLoading: (loading: boolean) => void;
  reset: () => void;
}

const initialState = {
  notifications: [],
  unreadCount: 0,
  isLoading: false,
  soundEnabled: true,
};

export const useNotificationStore = create<NotificationState>((set) => ({
  ...initialState,
  
  setNotifications: (notifications) => {
    const unreadCount = notifications.filter(n => !n.is_read).length;
    set({ notifications, unreadCount });
  },
  
  addNotification: (notification) => set((state) => ({
    notifications: [notification, ...state.notifications],
    unreadCount: notification.is_read ? state.unreadCount : state.unreadCount + 1,
  })),
  
  markAsRead: (notificationId) => set((state) => ({
    notifications: state.notifications.map(n =>
      n.id === notificationId ? { ...n, is_read: true } : n
    ),
    unreadCount: Math.max(0, state.unreadCount - 1),
  })),
  
  markAllAsRead: () => set((state) => ({
    notifications: state.notifications.map(n => ({ ...n, is_read: true })),
    unreadCount: 0,
  })),
  
  removeNotification: (notificationId) => set((state) => {
    const notification = state.notifications.find(n => n.id === notificationId);
    return {
      notifications: state.notifications.filter(n => n.id !== notificationId),
      unreadCount: notification && !notification.is_read
        ? Math.max(0, state.unreadCount - 1)
        : state.unreadCount,
    };
  }),
  
  setUnreadCount: (unreadCount) => set({ unreadCount }),
  setSoundEnabled: (soundEnabled) => set({ soundEnabled }),
  setLoading: (isLoading) => set({ isLoading }),
  reset: () => set(initialState),
}));
